"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { authService, agentService, User, Agent } from "@/services/api"
import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import AgentList from "@/components/AgentList"
import { useLanguage } from "@/contexts/LanguageContext"
import { 
  Bot, 
  Activity, 
  Plus,
  TrendingUp,
  Users,
  MessageSquare
} from "lucide-react"

export default function DashboardPage() {
  const { t } = useLanguage()
  const [user, setUser] = useState<User | null>(null)
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const [recentSessions, setRecentSessions] = useState<any[]>([])

  useEffect(() => {
    const token = localStorage.getItem("token")
    if (!token) {
      navigate("/login")
      return
    }

    const fetchData = async () => {
      try {
        const userResponse = await authService.getProfile()
        setUser(userResponse)
        
        // 获取用户的agents列表
        console.log('开始获取Agent列表...')
        const agentsResponse = await agentService.getAgentList()
        console.log('Agent列表API响应:', agentsResponse)
        console.log('Agent列表items:', agentsResponse.items)
        console.log('Agent列表长度:', agentsResponse.items?.length)
        
        setAgents(agentsResponse.items || [])
        console.log('设置到state的agents:', agentsResponse.items || [])

        // 获取最近会话
        const sessions = await agentService.getSessionList({ page: 0, limit: 5 })
        setRecentSessions(sessions)
      } catch (error: any) {
        console.error('获取数据失败:', error)
        if (error.response?.status === 401) {
          localStorage.removeItem("token")
          navigate("/login")
        } else {
          toast.error("Failed to load dashboard data")
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [navigate])

  const handleEditAgent = (agent: Agent) => {
    navigate(`/agent/${agent.id}`)
  }

  const handleDeleteAgent = async (agent: Agent) => {
    try {
      await agentService.deleteAgent(agent.id)
      setAgents(agents.filter(a => a.id !== agent.id))
      toast.success(`Agent "${agent.name}" deleted successfully`)
    } catch (error: any) {
      toast.error("Failed to delete agent")
    }
  }

  const handleToggleStatus = async (agent: Agent) => {
    try {
      const newStatus = agent.status === 'active' ? 'inactive' : 'active'
      await agentService.toggleAgentStatus(agent.id, newStatus)
      setAgents(agents.map(a => 
        a.id === agent.id 
          ? { ...a, status: newStatus }
          : a
      ))
      toast.success(`Agent "${agent.name}" ${newStatus === 'active' ? 'activated' : 'deactivated'}`)
    } catch (error: any) {
      toast.error("Failed to update agent status")
    }
  }

  const getStats = () => {
    const totalAgents = agents.length
    const activeAgents = agents.filter(a => a.status === 'active' || a.status === 'NORMAL').length
    const totalUsage = agents.reduce((sum, a) => sum + (a.usageCount || 0), 0)
    
    return { totalAgents, activeAgents, totalUsage }
  }

  const stats = getStats()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-20 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
            <p className="text-gray-600 mt-2">
              {t('dashboard.welcome')}, {user?.nickname || user?.account || user?.email}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.totalAgents')}</CardTitle>
                <Bot className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalAgents}</div>
                <p className="text-xs text-muted-foreground">
                  {t('dashboard.overviewDesc')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.activeAgents')}</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeAgents}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.activeAgents > 0 ? 'Currently running' : 'No active agents'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('dashboard.totalUsage')}</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalUsage.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  Total interactions
                </p>
              </CardContent>
            </Card>
          </div>

          {/* 最近会话模块 */}
          <Card className="mb-8">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center">
                <MessageSquare className="h-5 w-5 text-muted-foreground mr-2" />
                <CardTitle className="text-base font-semibold">{t('dashboard.recentActivity')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {recentSessions.length === 0 ? (
                <div className="text-gray-400 text-sm py-8 text-center">{t('dashboard.noActivity')}</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {recentSessions.map((session, idx) => (
                    <li key={session.sessionId || idx} className="py-3 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{session.title || `Session ${session.sessionId}`}</div>
                        <div className="text-xs text-gray-500 truncate mt-1">{session.lastMessageContent || ''}</div>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex flex-col items-end">
                        <span className="text-xs text-gray-400 mb-1">{session.lastMessageTime ? new Date(session.lastMessageTime).toLocaleString() : ''}</span>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/agent?sessionId=${session.sessionId}`)}>{t('dashboard.viewAll')}</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* My Agents Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('agent.myAgents')}</CardTitle>
              <CardDescription className="h-16 overflow-hidden text-ellipsis line-clamp-3">
                {t('agent.manageAgents')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AgentList 
                agents={agents}
                onEdit={handleEditAgent}
                onDelete={handleDeleteAgent}
                onToggleStatus={handleToggleStatus}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  )
}
